import type { SareeDTO } from '@neyara/shared';
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface CartItem {
  id: string;
  slug: string;
  name: string;
  price: number;
  image: string | null;
  quantity: number;
  /** Stock when the item was added; the cart caps quantity to it. */
  stock: number;
}

interface CartState {
  items: CartItem[];
  count: number;
  total: number;
  add: (saree: SareeDTO, quantity?: number) => void;
  setQuantity: (id: string, quantity: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const STORAGE_KEY = 'neyara.cart.v1';
const CartContext = createContext<CartState | null>(null);

function load(): CartItem[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(load);
  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Private mode or storage full: the cart still works for this visit.
    }
  }, [items]);

  // Keep tabs in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => e.key === STORAGE_KEY && setItems(load());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const add = useCallback((saree: SareeDTO, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === saree.id);
      const cap = Math.max(saree.stock, 0);
      if (existing) {
        return prev.map((i) =>
          i.id === saree.id ? { ...i, stock: cap, quantity: Math.min(i.quantity + quantity, cap) } : i,
        );
      }
      return [
        ...prev,
        {
          id: saree.id,
          slug: saree.slug,
          name: saree.name,
          price: saree.price,
          image: saree.images[0]?.url ?? null,
          stock: cap,
          quantity: Math.min(quantity, cap),
        },
      ];
    });
  }, []);

  const setQuantity = useCallback((id: string, quantity: number) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, Math.min(quantity, i.stock)) } : i)),
    );
  }, []);

  const remove = useCallback((id: string) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<CartState>(
    () => ({
      items,
      count: items.reduce((n, i) => n + i.quantity, 0),
      total: items.reduce((n, i) => n + i.quantity * i.price, 0),
      add,
      setQuantity,
      remove,
      clear,
      isOpen,
      open,
      close,
    }),
    [items, add, setQuantity, remove, clear, isOpen, open, close],
  );

  return <CartContext value={value}>{children}</CartContext>;
}

export function useCart() {
  const ctx = use(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
